// Pure, framework-free helpers for the Add Pick category/search UI.
// No lodash dependency (the global `_` in this app is unmanaged).

function label (row) {
  return `${row.AwayTeam} vs. ${row.HomeTeam} — ${row.MatchTime}`
}

function propLabel (row) {
  const name = row.Participant && row.Participant.Name ? row.Participant.Name : 'Player'
  return `${name} (${row.AwayTeam} vs. ${row.HomeTeam}) — ${row.PropType}`
}

// Build the ordered category list from the API payload.
// Each category: { key, label, mode: 'game'|'prop', rows: [] }
export function buildCategories (payload) {
  const cats = []
  cats.push({ key: 'Game', label: 'Game', mode: 'game', rows: (payload.games || []) })

  ;(payload.oddsTypes || []).forEach(ot => {
    cats.push({ key: ot.oddsType, label: ot.oddsType, mode: 'game', rows: (ot.games || []) })
  })

  if (payload.props && payload.props.length) {
    cats.push({ key: 'Props', label: 'Props', mode: 'prop', rows: payload.props })
  }
  return cats
}

// Filter a category's rows by a free-text query.
export function filterRows (category, query) {
  if (!category) return []
  const q = (query || '').trim().toLowerCase()
  if (!q) return category.rows
  if (category.mode === 'prop') {
    return category.rows.filter(r => {
      const name = (r.Participant && r.Participant.Name ? r.Participant.Name : '').toLowerCase()
      const prop = (r.PropType || '').toLowerCase()
      const teams = `${r.AwayTeam || ''} ${r.HomeTeam || ''}`.toLowerCase()
      return name.indexOf(q) !== -1 || prop.indexOf(q) !== -1 || teams.indexOf(q) !== -1
    })
  }
  return category.rows.filter(r => {
    const teams = `${r.AwayTeam || ''} ${r.HomeTeam || ''}`.toLowerCase()
    return teams.indexOf(q) !== -1
  })
}

// Display label for a row in the combobox, given the category mode.
export function rowLabel (category, row) {
  if (!row) return ''
  return category && category.mode === 'prop' ? propLabel(row) : label(row)
}

// Turn a camelCase PropType ("PassingYards") into readable text ("Passing Yards").
function humanizePropType (propType) {
  if (!propType) return ''
  return String(propType).replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

// Distinct games that have props, derived from the flattened props list.
// Each: { EventID, HomeTeam, AwayTeam, MatchTime } — shaped for the game combobox.
export function propGames (payload) {
  const seen = {}
  const games = []
  ;(payload.props || []).forEach(p => {
    const id = p.EventID
    if (id == null || seen[id]) return
    seen[id] = true
    games.push({
      ID: id,
      EventID: id,
      HomeTeam: p.HomeTeam,
      AwayTeam: p.AwayTeam,
      MatchTime: p.MatchTime,
    })
  })
  return games
}

// Props belonging to one game (by EventID).
export function propsForGame (payload, eventId) {
  if (eventId == null) return []
  return (payload.props || []).filter(p => p.EventID === eventId)
}

// Short label for a prop row once the game is already chosen: "Player — Passing Yards".
export function propRowLabel (row) {
  if (!row) return ''
  const name = row.Participant && row.Participant.Name ? row.Participant.Name : 'Player'
  return `${name} — ${humanizePropType(row.PropType)}`
}

// Filter a game's prop rows by player name or prop type (game is already fixed).
export function filterPropsInGame (rows, query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return rows || []
  return (rows || []).filter(r => {
    const name = (r.Participant && r.Participant.Name ? r.Participant.Name : '').toLowerCase()
    const prop = humanizePropType(r.PropType).toLowerCase()
    return name.indexOf(q) !== -1 || prop.indexOf(q) !== -1
  })
}

// A Yes/No prop (e.g. AnyTimeTouchdown) carries its two prices in the
// moneyline fields and has no over/under number — TotalNumber is 0/empty.
// An over/under prop (PassingYards, Strikeouts) has a real TotalNumber.
export function isYesNoProp (row) {
  if (!row) return false
  const o = (row.Odds && row.Odds[0]) || {}
  const total = parseFloat(o.TotalNumber)
  return !(total > 0)
}

// Build the human-readable pick string stored on the pick.
export function buildPickString (categoryKey, row, sideValue) {
  if (!row) return ''
  if (categoryKey === 'Props') {
    const name = row.Participant && row.Participant.Name ? row.Participant.Name : 'Player'
    const propText = humanizePropType(row.PropType)
    if (isYesNoProp(row)) {
      const yn = sideValue === 'no' ? 'NO' : 'YES'
      return `${name} ${yn} ${propText}`.replace(/\s+/g, ' ').trim()
    }
    const num = row.Odds && row.Odds[0] ? row.Odds[0].TotalNumber : ''
    const dir = sideValue === 'under' ? 'UNDER' : 'OVER'
    return `${name} ${dir} ${num} ${propText}`.replace(/\s+/g, ' ').trim()
  }
  // game / odds-type
  switch (sideValue) {
    case 'home': return row.HomeTeam
    case 'away': return row.AwayTeam
    case 'over': return `${row.AwayTeam} vs. ${row.HomeTeam} OVER`
    case 'under': return `${row.AwayTeam} vs. ${row.HomeTeam} UNDER`
    default: return ''
  }
}



// WEBPACK FOOTER //
// ./src/data/oddsCategories.js